/**
 * Wiki Filters & Sorting Configuration
 *
 * Keyed by `source` from the backend hierarchy (GET /wiki/types), NOT by URL slug or numeric id.
 * getSourceConfig merges the page's source chain top→bottom: excludeFilters/excludeSorting remove inherited,
 * filters/sorting add (dedup). FULL CONTROL: if no source in the chain is here, NO filters/sorting shown.
 *
 * Full explanation + "change filters on ONE page only" recipe: docs/wiki.md §3.
 */

export type FilterType = 'level' | 'rank' | 'crystal' | 'operatorType' | 'wikiItemLevel';
export type SortField = 'name' | 'lvl' | 'id' | 'price' | 'p_atk' | 'p_atk_spd' | 'm_atk' | 'pdef' | 'mdef' | 'wiki_item_level';
export type SortDirection = 'asc' | 'desc';

export interface FilterConfig {
  type: FilterType;
  paramName: string; // API parameter name
  /** Для `wikiItemLevel`: верхняя граница диапазона уровней в фильтре (опции 1..max). По умолчанию 10. */
  max?: number;
}

export interface SortConfig {
  field: SortField;
  labelKey: string; // i18n key
  defaultDirection: SortDirection;
}

export interface SourceConfig {
  filters?: FilterConfig[];
  sorting?: SortConfig[];
  // Exclude inherited filters/sorting by type/field
  excludeFilters?: FilterType[];
  excludeSorting?: SortField[];
}

/**
 * Configuration by source
 * Key = source value from hierarchy (matched during bottom-up traversal)
 */
export const wikiSourceConfig: Record<string, SourceConfig> = {
  // === WEAPONS ===
  weapon: {
    filters: [
      { type: 'crystal', paramName: 'crystalType' },
      { type: 'rank', paramName: 'rank' },
    ],
    sorting: [
      { field: 'p_atk', labelKey: 'sorting.byPAtk', defaultDirection: 'asc' },
      { field: 'p_atk_spd', labelKey: 'sorting.byPAtkSpd', defaultDirection: 'asc' },
    ],
  },

  // === MAGICAL WEAPONS ===
  magical: {
    excludeSorting: ['p_atk', 'p_atk_spd'],
    sorting: [
      { field: 'm_atk', labelKey: 'sorting.byMAtk', defaultDirection: 'asc' },
    ],
  },

  shields: {
    excludeSorting: ['p_atk', 'p_atk_spd'],
  },

  // === EQUIPMENT (armor parent) ===
  equipment: {
    filters: [
      { type: 'crystal', paramName: 'crystalType' },
      { type: 'rank', paramName: 'rank' },
    ],
    sorting: [
      { field: 'pdef', labelKey: 'sorting.byPDef', defaultDirection: 'asc' },
    ],
  },

  // === BIJOUTERIE (jewelry) ===
  bijouterie: {
    filters: [
      { type: 'rank', paramName: 'rank' },
      { type: 'crystal', paramName: 'crystalType' },
    ],
    sorting: [
      { field: 'mdef', labelKey: 'sorting.byMDef', defaultDirection: 'asc' },
    ],
  },

  // Подвеска (type 43) — единственный вид бижутерии, у которого вместо грейда/ранга фильтр по уровню.
  // `source: 'pendant'` уникален в иерархии (items → bijouterie → pendant), так что это ТОЛЬКО эта страница.
  pendant: {
    excludeFilters: ['crystal', 'rank'],
    filters: [
      { type: 'wikiItemLevel', paramName: 'wikiItemLevel' },
    ],
  },

  // === ITEMS ===
  items: {
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
    ],
  },

  // === ARMORS ===
  armors: {
    filters: [
      { type: 'rank', paramName: 'rank' },
      { type: 'crystal', paramName: 'crystalType' },
    ],
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
    ],
  },

  // === ACCESSORIES ===
  accessories: {
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
    ],
  },

  // === JEWELRY ===
  jewelry: {
    filters: [
      { type: 'rank', paramName: 'rank' },
    ],
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
    ],
  },

  // === BRACELETS ===
  "right bracelets": {
    filters: [
      { type: 'crystal', paramName: 'crystalType' },
      { type: 'rank', paramName: 'rank' },
    ],
  },

  "left bracelets": {
    filters: [
      { type: 'crystal', paramName: 'crystalType' },
      { type: 'rank', paramName: 'rank' },
    ],
  },

  // === TALISMANS ===
  talismans: {
    filters: [
      { type: 'wikiItemLevel', paramName: 'wikiItemLevel' },
    ],
    sorting: [
      { field: 'wiki_item_level', labelKey: 'sorting.byLevel', defaultDirection: 'asc' },
    ],
  },

  // === PRECIOUS STONES ===
  "precious stones": {
    filters: [{ type: 'wikiItemLevel', paramName: 'wiki_item_level', max: 18 }],
    sorting: [
      { field: 'wiki_item_level', labelKey: 'sorting.byLevel', defaultDirection: 'asc' },
    ],
  },

  // === CLASSES (skills hierarchy) ===
  classes: {
    filters: [
      { type: 'operatorType', paramName: 'operatorType' },
    ],
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
    ],
  },

  // === NPCs ===
  npc: {
    filters: [{ type: 'level', paramName: 'level' }],
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
      { field: 'lvl', labelKey: 'sorting.byLevel', defaultDirection: 'asc' },
    ],
  },

  // === ITEMS with level filter ===
  runes: {
    filters: [{ type: 'wikiItemLevel', paramName: 'wiki_item_level', max: 12 }],
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
      { field: 'wiki_item_level', labelKey: 'sorting.byLevel', defaultDirection: 'asc' },
    ],
  },

  pets: {
    filters: [{ type: 'wikiItemLevel', paramName: 'wiki_item_level' }],
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
      { field: 'wiki_item_level', labelKey: 'sorting.byLevel', defaultDirection: 'asc' },
    ],
  },

  "scrolls of modification": {
    filters: [{ type: 'wikiItemLevel', paramName: 'wiki_item_level' }],
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
      { field: 'wiki_item_level', labelKey: 'sorting.byLevel', defaultDirection: 'asc' },
    ],
  },

  elixirs: {
    filters: [{ type: 'wikiItemLevel', paramName: 'wiki_item_level' }],
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
      { field: 'wiki_item_level', labelKey: 'sorting.byLevel', defaultDirection: 'asc' },
    ],
  },

  potions: {
    filters: [{ type: 'wikiItemLevel', paramName: 'wiki_item_level' }],
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
      { field: 'wiki_item_level', labelKey: 'sorting.byLevel', defaultDirection: 'asc' },
    ],
  },

  "books/scrolls": {
    filters: [{ type: 'wikiItemLevel', paramName: 'wiki_item_level' }],
    sorting: [
      { field: 'name', labelKey: 'sorting.byName', defaultDirection: 'asc' },
      { field: 'wiki_item_level', labelKey: 'sorting.byLevel', defaultDirection: 'asc' },
    ],
  },

  "scrolls": {
      filters: [{ type: 'crystal', paramName: 'crystalType'}],
  },

  // === RECIPES ===
  recipes: {
    filters: [
      { type: 'crystal', paramName: 'crystalType' },
    ],
  },

  // === ENCHANTMENT SCROLLS ===
  "enchantment scrolls": {
    filters: [
      { type: 'crystal', paramName: 'crystalType' },
    ],
  },
};

/**
 * Get merged config for a source chain
 * Traverses from top to bottom, merging configs (deeper overrides/adds)
 *
 * @param sourceChain - Array of sources from hierarchy [category, subCategory, type, subType]
 * @returns SourceConfig or null if no config found
 */
export function getSourceConfig(sourceChain: (string | undefined | null)[]): SourceConfig | null {
  let mergedFilters: FilterConfig[] = [];
  let mergedSorting: SortConfig[] = [];
  let hasAnyConfig = false;

  // Traverse from beginning (top) to end (deepest)
  // Deeper configs add to parent configs
  for (let i = 0; i < sourceChain.length; i++) {
    const source = sourceChain[i];
    if (source && wikiSourceConfig[source]) {
      hasAnyConfig = true;
      const config = wikiSourceConfig[source];

      // Apply excludes first (remove from inherited)
      if (config.excludeFilters) {
        mergedFilters = mergedFilters.filter(f => !config.excludeFilters!.includes(f.type));
      }
      if (config.excludeSorting) {
        mergedSorting = mergedSorting.filter(s => !config.excludeSorting!.includes(s.field));
      }

      // Merge filters (avoid duplicates by type)
      if (config.filters) {
        for (const filter of config.filters) {
          if (!mergedFilters.some(f => f.type === filter.type)) {
            mergedFilters.push(filter);
          }
        }
      }

      // Merge sorting (avoid duplicates by field)
      if (config.sorting) {
        for (const sort of config.sorting) {
          if (!mergedSorting.some(s => s.field === sort.field)) {
            mergedSorting.push(sort);
          }
        }
      }
    }
  }

  if (!hasAnyConfig) {
    return null;
  }

  return {
    filters: mergedFilters.length > 0 ? mergedFilters : undefined,
    sorting: mergedSorting.length > 0 ? mergedSorting : undefined,
  };
}

/**
 * Check if any source in chain has filters
 */
export function hasFilters(sourceChain: (string | undefined | null)[]): boolean {
  const config = getSourceConfig(sourceChain);
  return !!(config?.filters && config.filters.length > 0);
}

/**
 * Check if any source in chain has sorting
 */
export function hasSorting(sourceChain: (string | undefined | null)[]): boolean {
  const config = getSourceConfig(sourceChain);
  return !!(config?.sorting && config.sorting.length > 0);
}
